import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger';

/**
 * Glass 2 (DESIGN.md §16.5): a capsule in the tone's own colour on its 15 % fill, no border, sentence
 * case. The fill tokens are the status colours at 13–17 %, so the pill is the colour it names.
 */
const TONE: Record<BadgeTone, string> = {
  neutral: 'bg-[var(--fill-tonal)] text-muted',
  accent: 'bg-accent-subtle text-accent-text',
  success: 'bg-success-subtle text-[color:var(--color-success)]',
  warning: 'bg-warning-subtle text-[color:var(--color-warning)]',
  danger: 'bg-danger-subtle text-[color:var(--color-danger)]',
};

/** `plain`: the same word as ordinary text — no fill (§16.5 punkt 7: «sjanger og lignende»). */
const PLAIN: Record<BadgeTone, string> = {
  neutral: 'text-default',
  accent: 'text-accent-text',
  success: 'text-[color:var(--color-success)]',
  warning: 'text-[color:var(--color-warning)]',
  danger: 'text-[color:var(--color-danger)]',
};

/**
 * - `pill` (standard): a status or a category that needs to be seen — «Godkjent», an agency.
 * - `plain`: the value is ordinary data (a genre, a type). It used to be a grey pill in a column of
 *   grey pills; now it is text, and colour is left to what means something.
 * - `count`: a number that is a deviation — how many are MISSING (§16.5 punkt 10: «22» in a red
 *   capsule, not red text beside a number). Round when the number is one digit. Tone defaults to
 *   `danger`; give `label` so a screen reader hears what the number counts.
 */
export type BadgeVariant = 'pill' | 'plain' | 'count';

export interface BadgeProps {
  tone?: BadgeTone;
  variant?: BadgeVariant;
  children: ReactNode;
  /** What a `count` counts, read instead of the bare number: «22 mangler folk». */
  label?: string;
  className?: string;
  /** Like `Card`'s: a badge is often the one thing on a row a test needs to point at. */
  'data-testid'?: string;
}

export function Badge({
  tone,
  variant = 'pill',
  children,
  label,
  className,
  'data-testid': testId,
}: BadgeProps): ReactNode {
  const resolved: BadgeTone = tone ?? (variant === 'count' ? 'danger' : 'neutral');

  if (variant === 'plain') {
    return (
      <span
        data-testid={testId}
        data-variant="plain"
        className={cn('text-sm', PLAIN[resolved], className)}
      >
        {children}
      </span>
    );
  }

  return (
    <span
      data-testid={testId}
      data-variant={variant}
      className={cn(
        'inline-flex h-5 items-center rounded-full font-medium whitespace-nowrap',
        variant === 'count'
          ? 'numeric min-w-5 justify-center px-1.5 text-2xs font-semibold'
          : 'px-2 text-xs',
        TONE[resolved],
        className,
      )}
    >
      {label === undefined ? (
        children
      ) : (
        <>
          <span aria-hidden>{children}</span>
          <span className="sr-only">{label}</span>
        </>
      )}
    </span>
  );
}
